import { CubeTimeDimensionGranularity } from '../../../../../common/enum/cube-time-dimension-granularity.enum';
import { Guard } from '../../../../../common/guards/guard';
import { LogManager } from '../../../../../common/port/log/log.manager.port';
import {
  convertWeekdayToWeekdayIndex,
  getFirstUtcDayInDateRange,
  isSameDate,
  isSameWeek,
  spreadDateRange,
  spreadDateRangeForWeekday,
} from '../../../../../common/utils/datetime.util';
import { SelfCalendarScoreTimeSeriesAggregateRoot } from '../../../../../core/domain/aggregates/calendar/time-series/self-calendar-score-time-series.aggregate-root';
import { SelfCalendarScoreTimeSeriesAggregateRootMapper } from '../../../../../infrastructure/mappers/calendar/score/time-series/self-calendar-score-time-series-aggregate-root.mapper';
import { GetSelfCalendarAppTimeSeriesRequestDto } from '../../../../dtos/calendar/score/time-series/get-calendar-app-time-series.request.dto';
import { GetSelfCalendarScoreTimeSeriesResponseDto } from '../../../../dtos/calendar/score/time-series/get-calendar-score-time-series.response.dto';
import { GetSelfCalendarAppTimeSeriesWeekdayQuery } from '../../../../queries/calendar/score/time-series/get-self-calendar-app-time-series-weekday.query';
import { GetSelfCalendarAppTimeSeriesQuery } from '../../../../queries/calendar/score/time-series/get-self-calendar-app-time-series.query';

export class GetSelfCalendarAppTimeSeriesController {
  constructor(
    protected readonly logManager: LogManager,
    protected readonly granularityQuery: GetSelfCalendarAppTimeSeriesQuery,
    protected readonly weekdayQuery: GetSelfCalendarAppTimeSeriesWeekdayQuery,
    protected readonly mapper: SelfCalendarScoreTimeSeriesAggregateRootMapper
  ) {}

  async get(
    props: GetSelfCalendarAppTimeSeriesRequestDto
  ): Promise<GetSelfCalendarScoreTimeSeriesResponseDto> {
    try {
      let result;
      let offset: number;
      let startDate: Date;

      const { segment, granularity } = props.params;

      const isGranularityEmpty = Guard.isEmpty(granularity);
      const isSegmentEmpty = Guard.isEmpty(segment);

      if (isGranularityEmpty && isSegmentEmpty) {
        throw new Error('No granularity or weekday parameters provided');
      }

      if (granularity) {
        result = await this.granularityQuery.execute(props);
        offset = granularity === CubeTimeDimensionGranularity.Day ? 1 : 7;
      }

      if (segment) {
        startDate = getFirstUtcDayInDateRange(
          new Date(props.params.startDate),
          new Date(props.params.endDate),
          convertWeekdayToWeekdayIndex(segment)
        );
        offset = 7;
        result = await this.weekdayQuery.execute(props);
      }

      const dates = granularity
        ? spreadDateRange(
            startDate || new Date(props.params.startDate),
            new Date(props.params.endDate),
            offset
          )
        : spreadDateRangeForWeekday(
            startDate || new Date(props.params.startDate),
            new Date(props.params.endDate),
            segment
          );

      const response: SelfCalendarScoreTimeSeriesAggregateRoot[] = [];
      for (const date of dates) {
        const reportsForDate = result.filter((res) =>
          granularity === CubeTimeDimensionGranularity.Day
            ? isSameDate(res.localStartDate, date)
            : isSameWeek(res.localStartDate, date)
        );
        response.push(
          SelfCalendarScoreTimeSeriesAggregateRoot.create({
            date,
            reports: reportsForDate,
          })
        );
      }

      return response.map((item) => this.mapper.toResponse(item));
    } catch (error) {
      throw new Error(
        `GetSelfCalendarScoreTimeSeriesAppController.get encountered an error ${error}`
      );
    }
  }
}
